// instances-tab.tsx — Compute instance list for the Operations service detail page.
// DB-only, no live vendor calls — shows instances from the local registry.
// Only rendered when service.provider.category === 'compute'.
import { RefreshCw, Server } from 'lucide-react';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Skeleton, Tooltip } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { useOpsServiceInstances, type OpsInstanceRow } from '@/api/operations';
import { formatDateTime } from '@/lib/format';
import { STATUS_TONE, STATUS_TOOLTIP } from './ops-detail-shared';

/* ── Column definitions ──────────────────────────────────────────────────── */

const instanceColumns: ColumnDef<OpsInstanceRow, unknown>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <span style={{ fontWeight: 500, color: 'var(--vn-fg-default)' }}>
        {row.original.name || '—'}
      </span>
    ),
  },
  {
    id: 'uid',
    accessorKey: 'uid',
    header: 'ID',
    cell: ({ row }) => (
      <span style={{ fontFamily: 'monospace', fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
        {row.original.uid || '—'}
      </span>
    ),
  },
  {
    id: 'zone_name',
    header: 'Zone',
    cell: ({ row }) =>
      row.original.zone_name ? (
        <Badge tone="neutral">{row.original.zone_name}</Badge>
      ) : (
        <span style={{ color: 'var(--vn-fg-subtle)' }}>—</span>
      ),
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const tone = STATUS_TONE[row.original.status] ?? 'neutral';
      const tip = STATUS_TOOLTIP[row.original.status] ?? row.original.status;
      return (
        <Tooltip content={tip}>
          <StatusPill tone={tone}>{row.original.status}</StatusPill>
        </Tooltip>
      );
    },
  },
  {
    id: 'created_at',
    accessorKey: 'created_at',
    header: 'Created',
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
        {formatDateTime(row.original.created_at)}
      </span>
    ),
  },
  {
    id: 'synchronized_at',
    header: 'Synced',
    cell: ({ row }) =>
      row.original.synchronized_at ? (
        <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
          {formatDateTime(row.original.synchronized_at)}
        </span>
      ) : (
        <span style={{ color: 'var(--vn-fg-subtle)' }}>—</span>
      ),
  },
];

/* ── Props ────────────────────────────────────────────────────────────────── */

interface InstancesTabProps {
  serviceId: string;
}

/* ── Component ───────────────────────────────────────────────────────────── */

/**
 * InstancesTab renders the compute instance registry for one service.
 * DB-only — no live vendor calls, so safe to auto-load on tab mount.
 * Lazy-mounted by the parent so fetches only fire when the tab is active.
 */
export function InstancesTab({ serviceId }: InstancesTabProps) {
  const instances = useOpsServiceInstances(serviceId);

  if (instances.isError) {
    return (
      <Alert
        tone="danger"
        title="Failed to load instances"
        actions={
          <Button size="sm" variant="ghost" onClick={() => instances.refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  if (instances.isLoading) {
    return (
      <Card>
        <CardBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 24 }} />
            ))}
          </div>
        </CardBody>
      </Card>
    );
  }

  const rows = instances.data ?? [];

  return (
    <Card>
      <CardHeader
        actions={
          <Button
            size="sm"
            variant="ghost"
            startIcon={<RefreshCw size={14} />}
            onClick={() => instances.refetch()}
            disabled={instances.isFetching}
          >
            {instances.isFetching ? 'Refreshing…' : 'Refresh'}
          </Button>
        }
      >
        <CardTitle>Instances ({rows.length})</CardTitle>
      </CardHeader>
      <CardBody style={{ padding: 0 }}>
        <DataTable
          columns={instanceColumns}
          data={rows}
          getRowId={(r) => r.id}
          dense
          emptyState={
            <EmptyState
              icon={<Server size={24} />}
              title="No instances"
              description="No instances found in the registry for this service."
            />
          }
          footer={
            rows.length > 0
              ? `${rows.length} instance${rows.length !== 1 ? 's' : ''} — from local registry (no live vendor call)`
              : undefined
          }
        />
      </CardBody>
    </Card>
  );
}
